/**
 * Customer Pre-Registration Type Definitions
 *
 * Backend同期:
 * - Model: app/Models/CustomerPreRegistration.php
 * - Resource: app/Http/Resources/V3/CustomerPreRegistrationResource.php
 *
 * @version 3.0.0
 */

// ============================================================================
// Enum Types
// ============================================================================

/**
 * 事前登録ステータス
 */
export type PreRegistrationStatus =
  | 'pending' // 未確認
  | 'confirmed' // 確認済み
  | 'converted' // 正式顧客変換済み
  | 'cancelled'; // キャンセル

/**
 * 性別
 */
export type Gender = 'male' | 'female' | 'other';

// ============================================================================
// Relation Types
// ============================================================================

/**
 * Store情報（Relation）
 */
export interface PreRegistrationStore {
  id: number;
  name: string;
  region: string | null;
  prefecture: string | null;
}

/**
 * Program情報（Relation）
 */
export interface PreRegistrationProgram {
  id: number;
  name: string;
  category?: {
    id: number;
    name: string;
  } | null;
}

/**
 * Converted Customer情報（Relation）
 */
export interface PreRegistrationConvertedCustomer {
  id: number;
  customer_number: string;
  name: string;
}

/**
 * Confirmed By User情報（Relation）
 */
export interface PreRegistrationConfirmedByUser {
  id: number;
  name: string;
  email: string;
}

// ============================================================================
// Main Entity Type
// ============================================================================

/**
 * 顧客事前登録メインエンティティ
 *
 * Backend: app/Models/CustomerPreRegistration.php
 * Resource: app/Http/Resources/V3/CustomerPreRegistrationResource.php
 */
export interface CustomerPreRegistration {
  // Primary Key
  id: number;

  // Store Information
  store_id: number;
  store?: PreRegistrationStore;

  // Basic Information
  avatar: string | null;
  avatar_url: string | null;
  family_name: string;
  given_name: string;
  full_name: string; // Appended attribute
  family_name_kana: string;
  given_name_kana: string;
  full_name_kana: string; // Appended attribute
  birth_date: string; // ISO8601 date format (YYYY-MM-DD)
  age: number | null; // Appended attribute
  gender: Gender;

  // Contact Information
  phone: string | null;
  mobile: string;
  email: string;

  // Address Information
  postal_code: string;
  region: string | null;
  prefecture: string;
  city: string;
  street_address: string;
  building: string | null;

  // Desired Information
  desired_program_id: number | null;
  desired_program?: PreRegistrationProgram | null;
  desired_join_date: string | null; // ISO8601 date format (YYYY-MM-DD)
  desired_visit_date: string | null; // ISO8601 date format (YYYY-MM-DD)
  notes: string | null;

  // Status Information
  status: PreRegistrationStatus;
  converted_customer_id: number | null;
  converted_customer?: PreRegistrationConvertedCustomer | null;
  converted_at: string | null; // ISO8601 datetime

  // Confirmation Information
  confirmed_by: number | null;
  confirmed_by_user?: PreRegistrationConfirmedByUser | null;
  confirmed_at: string | null; // ISO8601 datetime

  // Metadata
  created_at: string; // ISO8601 datetime
  updated_at: string; // ISO8601 datetime
  metadata?: {
    created_at: string;
    updated_at: string;
    is_pending: boolean;
    is_confirmed: boolean;
    is_converted: boolean;
    is_cancelled: boolean;
  };
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * 事前登録一覧APIレスポンス型
 */
export interface CustomerPreRegistrationListResponse {
  data: CustomerPreRegistration[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
    version?: string;
    resource?: string;
    timestamp?: string;
  };
}

/**
 * 事前登録詳細APIレスポンス型
 */
export interface CustomerPreRegistrationDetailResponse {
  data: CustomerPreRegistration;
  meta?: {
    version: string;
    resource: string;
    timestamp: string;
  };
}

/**
 * 事前登録統計APIレスポンス型
 */
export interface CustomerPreRegistrationStats {
  total: number;
  pending_count: number;
  confirmed_count: number;
  converted_count: number;
  cancelled_count: number;
  this_month: number;
  this_week: number;
  today: number;
}

/**
 * 統計APIレスポンス型
 */
export interface CustomerPreRegistrationStatsResponse {
  data: CustomerPreRegistrationStats;
  meta?: {
    version: string;
    resource: string;
    timestamp: string;
  };
}

// ============================================================================
// Form Input Types
// ============================================================================

/**
 * 事前登録フォーム入力型（公開フォーム用）
 *
 * 用途: 公開Webフォームからの新規事前登録作成
 */
export interface CustomerPreRegistrationFormInput {
  // Store
  store_id: number;

  // Basic Information
  avatar?: File | null;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  birth_date: string; // YYYY-MM-DD format
  gender: Gender;

  // Contact Information
  phone?: string;
  mobile: string;
  email: string;

  // Address Information
  postal_code: string;
  region?: string;
  prefecture: string;
  city: string;
  street_address: string;
  building?: string;

  // Desired Information
  desired_program_id?: number;
  desired_join_date?: string; // YYYY-MM-DD format
  desired_visit_date?: string; // YYYY-MM-DD format
  notes?: string;

  // Confirmation (必須チェックボックス)
  confirmation: boolean;
}

/**
 * 管理画面用事前登録作成/更新フォーム入力型
 *
 * 用途: 管理画面での事前登録CRUD操作
 */
export interface CustomerPreRegistrationAdminFormInput {
  // Store
  store_id: number;

  // Basic Information
  avatar?: File | string | null; // File(新規) | string(URL既存) | null(削除)
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  birth_date: string; // YYYY-MM-DD format
  gender: Gender;

  // Contact Information
  phone?: string;
  mobile: string;
  email: string;

  // Address Information
  postal_code: string;
  region?: string;
  prefecture: string;
  city: string;
  street_address: string;
  building?: string;

  // Desired Information
  desired_program_id?: number;
  desired_join_date?: string; // YYYY-MM-DD format
  desired_visit_date?: string; // YYYY-MM-DD format
  notes?: string;

  // Status (管理画面でのみ編集可能)
  status?: PreRegistrationStatus;
}

/**
 * ステータス更新フォーム入力型
 */
export interface CustomerPreRegistrationStatusUpdateInput {
  status: PreRegistrationStatus;
  notes?: string; // ステータス変更時のメモ
}

/**
 * 正式顧客変換フォーム入力型
 */
export interface CustomerPreRegistrationConvertInput {
  // 変換先プログラム情報
  program_id?: number;
  join_date?: string; // YYYY-MM-DD format

  // 顧客番号（自動生成 or 手動指定）
  customer_number?: string;

  // メモ
  notes?: string;
}

// ============================================================================
// Filter & Search Types
// ============================================================================

/**
 * 事前登録フィルター型
 */
export interface CustomerPreRegistrationFilters {
  // ステータス
  status?: PreRegistrationStatus | PreRegistrationStatus[];

  // 店舗
  store_id?: number | number[];

  // 検索キーワード（氏名、カナ、メール、電話）
  search?: string;

  // 登録日範囲
  created_from?: string; // YYYY-MM-DD
  created_to?: string; // YYYY-MM-DD

  // 希望入会日範囲
  desired_join_date_from?: string; // YYYY-MM-DD
  desired_join_date_to?: string; // YYYY-MM-DD

  // 希望来店日範囲
  desired_visit_date_from?: string; // YYYY-MM-DD
  desired_visit_date_to?: string; // YYYY-MM-DD

  // 希望プログラム
  desired_program_id?: number | number[];

  // ページネーション
  page?: number;
  per_page?: number;

  // ソート
  sort_by?: 'created_at' | 'updated_at' | 'family_name' | 'desired_visit_date';
  sort_order?: 'asc' | 'desc';
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * 事前登録作成用型（Omit ID/Timestamps）
 */
export type CustomerPreRegistrationCreateInput = Omit<
  CustomerPreRegistration,
  | 'id'
  | 'full_name'
  | 'full_name_kana'
  | 'avatar_url'
  | 'age'
  | 'converted_customer_id'
  | 'converted_at'
  | 'confirmed_by'
  | 'confirmed_at'
  | 'created_at'
  | 'updated_at'
  | 'metadata'
  | 'store'
  | 'desired_program'
  | 'converted_customer'
  | 'confirmed_by_user'
>;

/**
 * 事前登録更新用型（Partial）
 */
export type CustomerPreRegistrationUpdateInput = Partial<CustomerPreRegistrationCreateInput>;

/**
 * ステータス判定ヘルパー型
 */
export interface PreRegistrationStatusHelpers {
  isPending: (status: PreRegistrationStatus) => boolean;
  isConfirmed: (status: PreRegistrationStatus) => boolean;
  isConverted: (status: PreRegistrationStatus) => boolean;
  isCancelled: (status: PreRegistrationStatus) => boolean;
  canConfirm: (status: PreRegistrationStatus) => boolean;
  canConvert: (status: PreRegistrationStatus) => boolean;
  canCancel: (status: PreRegistrationStatus) => boolean;
}

// ============================================================================
// Export Constants
// ============================================================================

/**
 * ステータスラベルマッピング
 */
export const PRE_REGISTRATION_STATUS_LABELS: Record<
  PreRegistrationStatus,
  string
> = {
  pending: '未確認',
  confirmed: '確認済み',
  converted: '変換済み',
  cancelled: 'キャンセル',
} as const;

/**
 * ステータスカラーマッピング（Mantine色）
 */
export const PRE_REGISTRATION_STATUS_COLORS: Record<
  PreRegistrationStatus,
  string
> = {
  pending: 'yellow',
  confirmed: 'blue',
  converted: 'green',
  cancelled: 'gray',
} as const;

/**
 * 性別ラベルマッピング
 */
export const GENDER_LABELS: Record<Gender, string> = {
  male: '男性',
  female: '女性',
  other: 'その他',
} as const;

/**
 * デフォルトページネーション設定
 */
export const DEFAULT_PRE_REGISTRATION_PAGINATION = {
  page: 1,
  per_page: 20,
} as const;
